// HiStrips-style pack picker (owner brief, 2026-10-29/30): wraps the whole buy box
// (headline price + pack tiles + add-to-cart button), so one listener can update
// all three when a tile is selected. Tier figures come from the JSON payload in
// [data-tier-pricing] (server-rendered, Shopify-money-formatted — this file never
// computes or formats currency itself).
//
// Critical fix (2026-10-30): the native product form's quantity field ADDS to any
// existing cart line for this variant — picking "2 boxes" when 2 are already in the
// cart would make 4, not 2. The tiles' radios carry the tier number as [name="tier"]
// (selection/styling only, never submitted). Submission is handled entirely here via
// /cart/change.js, which SETS a line to an absolute quantity rather than adding to
// it — tried rewriting the native form's quantity field to a computed delta and
// letting Standard Actions' own AJAX submit handle it, but that path reads/caches
// the cart state in ways this file can't see or reliably race against, and produced
// wrong totals in testing. Setting the line directly removes that uncertainty
// entirely, at the cost of a full page reload instead of an in-place AJAX update.
class BundlePriceSync extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-tier-pricing]');
    if (!dataEl) return;

    try {
      this.tiers = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    this.unitPriceEl = this.querySelector('[data-tier-unit-price]');
    this.saveBadgeEl = this.querySelector('[data-tier-save-badge]');
    this.saveSrEl = this.querySelector('[data-tier-save-sr]');
    this.totalLineEl = this.querySelector('[data-tier-total-line]');
    this.labelEl = this.querySelector('[data-add-to-cart-label]');
    this.variantInput = this.querySelector('[name="id"]');
    this.submitButton = this.querySelector('[data-add-to-cart-label]')?.closest('button');
    this.form = this.querySelector('form');

    this.selectedQty = 1;
    const checkedTier = this.querySelector('[name="tier"]:checked');
    if (checkedTier) this.selectedQty = parseInt(checkedTier.value, 10) || 1;

    this.addEventListener('change', (event) => {
      if (event.target.name !== 'tier') return;
      this.selectedQty = parseInt(event.target.value, 10) || 1;
      this.applyTier(event.target.value);
    });

    // Capture phase on the ancestor custom element, not the form directly: this
    // guarantees our handler runs, and preventDefault takes effect, before
    // s-product-form's own submit handling (attached to the form itself, in the
    // target/bubble phase), regardless of which custom element upgraded first.
    this.addEventListener('submit', (event) => this.handleSubmit(event), { capture: true });
  }

  async handleSubmit(event) {
    event.preventDefault();
    // preventDefault alone only blocks the browser's native navigation — it does
    // NOT stop s-product-form's own submit listener (attached directly to the form)
    // from also running and firing a second, uncorrected add. Capture-phase
    // stopPropagation keeps the event from ever reaching the form at all.
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (!this.variantInput || this.submitting) return;

    this.submitting = true;
    if (this.submitButton) this.submitButton.setAttribute('aria-busy', 'true');

    let currentQuantity = 0;
    try {
      const cart = await fetch('/cart.js').then((response) => response.json());
      const line = cart.items.find((item) => String(item.variant_id) === String(this.variantInput.value));
      currentQuantity = line ? line.quantity : 0;
    } catch (error) {
      currentQuantity = 0;
    }

    if (this.selectedQty === currentQuantity) {
      // Already exactly at the selected tier: nothing to change. Reassure the
      // shopper by opening the cart instead of silently doing nothing.
      document.getElementById('cart-drawer')?.showModal?.();
      this.submitting = false;
      if (this.submitButton) this.submitButton.removeAttribute('aria-busy');
      return;
    }

    if (currentQuantity === 0) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this.variantInput.value, quantity: this.selectedQty }] }),
      }).catch(() => {});
    } else {
      // SETS the line to an absolute quantity (never adds/subtracts), covering
      // both increases and decreases from whatever's already in the cart.
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.variantInput.value, quantity: this.selectedQty }),
      }).catch(() => {});
    }

    window.location.reload();
  }

  applyTier(value) {
    const tier = this.tiers[value];
    if (!tier) return;

    if (this.unitPriceEl) this.unitPriceEl.textContent = tier.unit;
    if (this.saveBadgeEl) this.saveBadgeEl.textContent = `(-${tier.savePercent}%)`;
    if (this.saveSrEl) this.saveSrEl.textContent = `Save ${tier.savePercent}%`;
    if (this.totalLineEl) this.totalLineEl.textContent = tier.totalLine;

    if (this.labelEl) {
      const baseText = this.labelEl.dataset.baseText;
      this.labelEl.textContent = `${baseText} · ${tier.total}`;
    }
  }
}

customElements.define('bundle-price-sync', BundlePriceSync);
